import { ReactNode, useEffect, useState } from 'react';
import { EventCapacityData, EventCapacityStatus, ReservationModel, ReservationUpdate } from './contracts';
import { FormEventAPIHelpers, FormEventContext } from './form-event-context';
export const FormEventContextProvider = (
	props: FormEventAPIHelpers & { children: ReactNode; formRequestId: string; ownerId?: string; event?: EventCapacityData; token?: string }
) => {
	const {
		children,
		event,
		ownerId,
		updateReservationCount,
		createReservation,
		extendReservation,
		validateRsvpResponse,
		token,
		formRequestId,
		...formEventApiHelpers
	} = props;

	const [eventCapacityData, setEventCapacityData] = useState<EventCapacityData | undefined>(event);
	const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
	const updateReservationCountAndSetState = async (input: { operation: ReservationUpdate; count: number }): Promise<boolean> => {
		const { count, operation } = input;
		const reservationId = eventCapacityData?.reservation?.id;
		if (!reservationId) return false;
		const result = await updateReservationCount?.({ formRequestId, reservationId, count, operation, token });
		if (result) {
			setEventCapacityData(prev => {
				if (prev?.reservation?.id && typeof result === 'number') {
					return {
						...prev,
						reservation: {
							...prev.reservation,
							count: result
						}
					};
				} else {
					return prev;
				}
			});
			return true;
		} else {
			return false;
			//if error set some state to show error?
		}
	};
	const createReservationAndSetState = async (count: number = 1): Promise<void> => {
		if (!ownerId) return;
		const result = await createReservation({ eventId: ownerId, token, count });
		if (result) {
			setEventCapacityData(result);
		} else {
			//if error set some state to show error?
		}
	};

	//used for ticket and event, for the ticket we need to return the object, for event we need to set the capacity data
	const extendReservationAndSetState = async (input: {
		messageKey: string;
		reservationId?: string;
		safeExpiry?: boolean;
	}): Promise<{ success: boolean; reservation?: ReservationModel; message?: string }> => {
		const { messageKey, safeExpiry, reservationId: reservationIdInput } = input;
		const reservationId = reservationIdInput ?? eventCapacityData?.reservation?.id;
		if (!reservationId) return { success: false, message: 'No reservation id provided.' };
		const result = await extendReservation({ formRequestId, reservationId, messageKey, safeExpiry, token });
		if (result?.success) {
			setEventCapacityData(prev =>
				prev
					? {
							...prev,
							reservation: result?.reservation
						}
					: prev
			);
		} else {
			//set some sort of error
		}
		return result ?? { success: false, message: 'Something went wrong extending the reservation.' };
	};

	const validateRsvpResponseContext = async (): Promise<boolean> => {
		if (eventCapacityData?.ticketsEnabled) return true;
		const reservationId = eventCapacityData?.reservation?.id;
		if (!reservationId) return false;
		const result = await validateRsvpResponse({ reservationId, token });
		if (!result) return false;
		return true;
	};

	useEffect(() => {
		const createRes = async () => {
			if (
				ownerId &&
				token &&
				(eventCapacityData?.status === EventCapacityStatus.Available || eventCapacityData?.status === EventCapacityStatus.OnWaitlist)
			) {
				await createReservationAndSetState();
			}
		};
		if (!eventCapacityData?.ticketsEnabled) {
			// Creates a reservation for older forms that have capacity data without tickets
			createRes();
		}
	}, [token, eventCapacityData?.ticketsEnabled]);

	return (
		<FormEventContext.Provider
			value={{
				...formEventApiHelpers,
				event: eventCapacityData,
				setEventCapacityData,
				waitlistSubmitted,
				setWaitlistSubmitted,
				updateReservationCount: updateReservationCountAndSetState,
				extendReservation: extendReservationAndSetState,
				validateRsvpResponse: validateRsvpResponseContext,
				createReservation: createReservationAndSetState
			}}
		>
			{children}
		</FormEventContext.Provider>
	);
};
